import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ProductRecommendations } from '@/components/Analytics';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Loader2, ShoppingCart, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${slug}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    try {
      await addToCart(product.id, quantity);
      toast.success(`Added ${quantity} item(s) to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex justify-center items-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex-1 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-detail-image"
              />
            </div>

            <div className="space-y-6">
              <div>
                <Badge className="bg-success text-white mb-4" data-testid="eggless-badge">
                  100% Eggless
                </Badge>
                <h1 className="text-3xl md:text-4xl font-bold text-accent mb-2" data-testid="product-detail-name">
                  {product.name}
                </h1>
                {product.weight && (
                  <p className="text-muted-foreground text-lg">{product.weight}</p>
                )}
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed" data-testid="product-detail-description">
                {product.description}
              </p>

              <div className="flex items-baseline space-x-2">
                <span className="text-4xl font-bold text-primary" data-testid="product-detail-price">
                  ₹{product.price}
                </span>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="h-10 w-10 rounded-full border-amber-200"
                    data-testid="decrease-quantity"
                  >
                    -
                  </Button>
                  <span className="text-xl font-semibold w-12 text-center" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(quantity + 1)}
                    className="h-10 w-10 rounded-full border-amber-200"
                    data-testid="increase-quantity"
                  >
                    +
                  </Button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90 font-medium transition-all hover:scale-105 active:scale-95 shadow-lg"
                  onClick={handleAddToCart}
                  data-testid="add-to-cart-button"
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Add to Cart
                </Button>
              </div>

              <div className="bg-amber-50 rounded-xl p-6 space-y-3">
                <h3 className="font-semibold text-accent">Product Features</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-center">
                    <span className="mr-2">✓</span> 100% Eggless
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span> Fresh Daily
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span> High Quality Ingredients
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span> Made with Love
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <ProductRecommendations productId={product.id} />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;
